import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import { useNavigate } from 'react-router-dom';
import { Box, Card, ClickAwayListener, Grid, Paper, Popper, Typography, CircularProgress, Alert, Button, IconButton, Chip, useTheme } from '@mui/material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import './Dashboard.css';

const PIE_COLORS = ['#10b981', '#f59e0b'];

const Dashboard = () => {
  const theme = useTheme();
  const COLORS = {
    primary: theme.palette.text.primary,
    accent: '#0d9488',
    accentLight: '#14b8a6',
    warning: '#f59e0b',
    success: '#10b981',
    neutral: theme.palette.text.secondary,
    border: theme.palette.divider
  };

  const TOOLTIP_STYLE = {
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    fontSize: 12,
    color: theme.palette.text.primary
  };
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const saved = sessionStorage.getItem('dashboardSelectedMonth');
    return saved ? dayjs(saved) : dayjs();
  });
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerAnchorRef = useRef(null);
  const navigate = useNavigate();

  // Persist selected month for the duration of the browser session
  useEffect(() => {
    sessionStorage.setItem('dashboardSelectedMonth', selectedMonth.format('YYYY-MM-DD'));
  }, [selectedMonth]);

  const isCurrentMonth = selectedMonth.isSame(dayjs(), 'month');

  const fechaDesde = selectedMonth.startOf('month').format('YYYY-MM-DD');
  const fechaHasta = isCurrentMonth ? dayjs().format('YYYY-MM-DD') : selectedMonth.endOf('month').format('YYYY-MM-DD');

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('horas', {
        params: {
          fechaDesde,
          fechaHasta
        }
      });

      const horasData = Array.isArray(response.data) ? response.data : [];
      setData(aggregateDashboardMetrics(horasData));
      setError(null);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('No se pudieron cargar los datos del dashboard');
    } finally {
      setLoading(false);
    }
  }, [fechaDesde, fechaHasta]);

  useEffect(() => {
    dayjs.locale('es');
    fetchDashboardData();
  }, [fetchDashboardData]);

  const aggregateDashboardMetrics = (horasData) => {
    const safeHorasData = Array.isArray(horasData) ? horasData : [];
    const totalHoras = safeHorasData.reduce((sum, h) => sum + (parseFloat(h.horas) || 0), 0);

    // Pending Payment
    const pendingData = safeHorasData.filter((h) => !h.pagado);
    const pendingAmount = pendingData.reduce((sum, h) => {
      const amount = parseFloat(h.horas) * parseFloat(h.costo);
      return sum + (isNaN(amount) ? 0 : amount);
    }, 0);

    // Revenue This Month (Paid)
    const paidData = safeHorasData.filter((h) => h.pagado);
    const paidAmount = paidData.reduce((sum, h) => {
      const amount = parseFloat(h.horas) * parseFloat(h.costo);
      return sum + (isNaN(amount) ? 0 : amount);
    }, 0);

    // Utilization Rate (assuming 160 billable hours/month)
    const utilizationRate = Math.round((totalHoras / 160) * 100);

    // Top Customers — preserve customer_id for navigation
    const customerMap = {};
    safeHorasData.forEach((h) => {
      const name = h.customer_name || 'Sin nombre';
      if (!customerMap[name]) {
        customerMap[name] = { horas: 0, customerId: h.customer_id };
      }
      customerMap[name].horas += parseFloat(h.horas) || 0;
    });
    const topCustomers = Object.entries(customerMap)
      .map(([name, { horas, customerId }]) => ({ name, horas: parseFloat(horas.toFixed(2)), customerId }))
      .sort((a, b) => b.horas - a.horas)
      .slice(0, 5);

    // Hours Trend
    const trendMap = {};
    safeHorasData.forEach((h) => {
      const dateKey = dayjs(h.dia).format('DD/MM');
      const sortKey = dayjs(h.dia).valueOf();
      if (!trendMap[dateKey]) {
        trendMap[dateKey] = { horas: 0, sortKey };
      }
      trendMap[dateKey].horas += parseFloat(h.horas) || 0;
    });
    const hoursTrend = Object.entries(trendMap)
      .map(([date, { horas, sortKey }]) => ({ date, horas: parseFloat(horas.toFixed(2)), sortKey }))
      .sort((a, b) => a.sortKey - b.sortKey)
      .map(({ date, horas }) => ({ date, horas }));

    // Payment Status
    const paymentStatus = [
      { name: 'Pagado', value: paidData.length },
      { name: 'Pendiente', value: pendingData.length }
    ];

    // Top Items — preserve item_id for navigation
    const itemMap = {};
    safeHorasData.forEach((h) => {
      const name = h.item_name || 'Sin nombre';
      if (!itemMap[name]) {
        itemMap[name] = { horas: 0, itemId: h.item_id };
      }
      itemMap[name].horas += parseFloat(h.horas) || 0;
    });
    const topItems = Object.entries(itemMap)
      .map(([name, { horas, itemId }]) => ({ name, horas: parseFloat(horas.toFixed(2)), itemId }))
      .sort((a, b) => b.horas - a.horas)
      .slice(0, 5);

    return {
      totalHoras: parseFloat(totalHoras.toFixed(2)),
      pendingAmount: parseFloat(pendingAmount.toFixed(2)),
      paidAmount: parseFloat(paidAmount.toFixed(2)),
      utilizationRate,
      topCustomers,
      hoursTrend,
      paymentStatus,
      topItems,
      pendingCount: pendingData.length,
      paidCount: paidData.length
    };
  };

  const navigateToReporte = (extras = {}) => {
    navigate('/reporte-horas', { state: { fechaDesde, fechaHasta, ...extras } });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" sx={{ minHeight: 300 }}>
        <CircularProgress sx={{ color: COLORS.accent }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={fetchDashboardData}>
          Reintentar
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Month selector */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            background: theme.palette.background.paper,
            border: `1px solid ${COLORS.border}`,
            borderRadius: 3,
            px: 1,
            py: 0.5
          }}>
          <IconButton size="small" onClick={() => setSelectedMonth((m) => m.subtract(1, 'month'))}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>

          {/* Clickable month label — opens custom year/month picker */}
          <Box
            ref={pickerAnchorRef}
            onClick={() => setPickerOpen((o) => !o)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              cursor: 'pointer',
              px: 0.5,
              py: 0.25,
              borderRadius: 1,
              userSelect: 'none',
              '&:hover': { bgcolor: 'action.hover' }
            }}>
            <CalendarMonthIcon sx={{ fontSize: 15, color: pickerOpen ? COLORS.accent : COLORS.neutral }} />
            <Typography
              sx={{
                minWidth: 130,
                textAlign: 'center',
                fontWeight: 600,
                color: pickerOpen ? COLORS.accent : COLORS.primary,
                fontSize: '0.9rem',
                textTransform: 'capitalize',
                transition: 'color 0.15s'
              }}>
              {selectedMonth.locale('es').format('MMMM YYYY')}
            </Typography>
          </Box>

          <IconButton size="small" onClick={() => setSelectedMonth((m) => m.add(1, 'month'))} disabled={isCurrentMonth}>
            <ChevronRightIcon fontSize="small" />
          </IconButton>
          {!isCurrentMonth && (
            <Chip
              label="Hoy"
              size="small"
              onClick={() => setSelectedMonth(dayjs())}
              sx={{
                height: 22,
                fontSize: '0.72rem',
                bgcolor: COLORS.accent,
                color: '#fff',
                cursor: 'pointer',
                '&:hover': { bgcolor: '#0f766e' }
              }}
            />
          )}
        </Box>

        <MonthPickerPopover
          open={pickerOpen}
          anchorEl={pickerAnchorRef.current}
          value={selectedMonth}
          maxDate={dayjs()}
          onChange={(newMonth) => {
            setSelectedMonth(newMonth);
            setPickerOpen(false);
          }}
          onClose={() => setPickerOpen(false)}
        />
      </Box>

      {/* Metric Cards */}
      <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Horas este mes"
            value={`${data.totalHoras}h`}
            icon={<ScheduleIcon />}
            color={COLORS.accent}
            onClick={() => navigateToReporte()}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Pendiente de pago"
            value={`$${data.pendingAmount.toLocaleString('es-ES', { maximumFractionDigits: 2 })}`}
            subtitle={`${data.pendingCount} registros`}
            icon={<WarningAmberIcon />}
            color={COLORS.warning}
            onClick={() => navigateToReporte({ pagado: false })}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Ingresos pagados"
            value={`$${data.paidAmount.toLocaleString('es-ES', { maximumFractionDigits: 2 })}`}
            subtitle={`${data.paidCount} registros`}
            icon={<CheckCircleOutlineIcon />}
            color={COLORS.success}
            onClick={() => navigateToReporte({ pagado: true })}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Tasa de utilización"
            value={`${data.utilizationRate}%`}
            subtitle="De 160h mensuales"
            icon={<TrendingUpIcon />}
            color={
              data.utilizationRate >= 75 ? COLORS.success : data.utilizationRate >= 50 ? COLORS.warning : '#ef4444'
            }
          />
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={2.5}>
        <Grid item xs={12} md={6}>
          <Card className="chart-card">
            <Typography variant="h6" className="chart-title">
              Horas por día
            </Typography>
            {data.hoursTrend.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={data.hoursTrend} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
                  <XAxis dataKey="date" stroke={COLORS.neutral} tick={{ fontSize: 11 }} />
                  <YAxis stroke={COLORS.neutral} tick={{ fontSize: 11 }} />
                  <Tooltip
                    contentStyle={TOOLTIP_STYLE}
                    formatter={(v) => [`${v}h`, 'Horas']}
                  />
                  <Line
                    type="monotone"
                    dataKey="horas"
                    stroke={COLORS.accent}
                    strokeWidth={2.5}
                    dot={{ fill: COLORS.accent, r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <EmptyState />
            )}
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card className="chart-card">
            <Typography variant="h6" className="chart-title">
              Top clientes
            </Typography>
            <Typography variant="caption" sx={{ color: COLORS.neutral, display: 'block', mb: 1.5 }}>
              Clic en una barra para ver el reporte
            </Typography>
            {data.topCustomers.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.topCustomers} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
                  <XAxis dataKey="name" stroke={COLORS.neutral} tick={{ fontSize: 11 }} />
                  <YAxis stroke={COLORS.neutral} tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v) => [`${v}h`, 'Horas']} />
                  <Bar
                    dataKey="horas"
                    fill={COLORS.accent}
                    radius={[6, 6, 0, 0]}
                    cursor="pointer"
                    onClick={(d) => navigateToReporte({ customerIds: [d.customerId] })}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyState />
            )}
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card className="chart-card">
            <Typography variant="h6" className="chart-title">
              Estado de pago
            </Typography>
            {data.paymentStatus[0].value > 0 || data.paymentStatus[1].value > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={data.paymentStatus}
                    cx="50%"
                    cy="50%"
                    outerRadius={95}
                    labelLine={false}
                    label={({ name, value, percent }) =>
                      value > 0 ? `${name}: ${value} (${(percent * 100).toFixed(0)}%)` : ''
                    }
                    dataKey="value"
                    cursor="pointer"
                    onClick={(entry) => entry && navigateToReporte({ pagado: entry.name === 'Pagado' })}>
                    {data.paymentStatus.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v) => [`${v} registros`]} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <EmptyState />
            )}
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card className="chart-card">
            <Typography variant="h6" className="chart-title">
              Top servicios
            </Typography>
            <Typography variant="caption" sx={{ color: COLORS.neutral, display: 'block', mb: 1.5 }}>
              Clic en una barra para ver el reporte
            </Typography>
            {data.topItems.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.topItems} layout="vertical" margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
                  <XAxis type="number" stroke={COLORS.neutral} tick={{ fontSize: 11 }} />
                  <YAxis dataKey="name" type="category" stroke={COLORS.neutral} width={130} tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v) => [`${v}h`, 'Horas']} />
                  <Bar
                    dataKey="horas"
                    fill={COLORS.accentLight}
                    radius={[0, 6, 6, 0]}
                    cursor="pointer"
                    onClick={(d) => navigateToReporte({ itemIds: [d.itemId] })}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyState />
            )}
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

// ─── Custom Month/Year Picker ─────────────────────────────────────────────────
const MONTH_LABELS_ES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const MonthPickerPopover = ({ open, anchorEl, value, maxDate, onChange, onClose }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [viewYear, setViewYear] = useState(value.year());

  // Keep viewYear in sync if external value changes (e.g. arrow nav)
  useEffect(() => {
    if (open) setViewYear(value.year());
  }, [open, value]);

  const maxYear = (maxDate || dayjs()).year();
  const maxMonth = (maxDate || dayjs()).month(); // 0-indexed

  const isDisabled = (monthIdx) => viewYear > maxYear || (viewYear === maxYear && monthIdx > maxMonth);
  const isSelected = (monthIdx) => value.year() === viewYear && value.month() === monthIdx;

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="bottom-end"
      modifiers={[{ name: 'offset', options: { offset: [0, 6] } }]}
      sx={{ zIndex: 1300 }}>
      <ClickAwayListener onClickAway={onClose}>
        <Paper
          elevation={8}
          sx={{
            p: 1.5,
            width: 224,
            borderRadius: '12px',
            border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
            bgcolor: 'background.paper',
            boxShadow: isDark
              ? '0 8px 32px rgba(0,0,0,0.5)'
              : '0 8px 32px rgba(15,23,42,0.12)',
            transformOrigin: 'top right',
            animation: 'monthPickerIn 0.15s ease',
            '@keyframes monthPickerIn': {
              from: { opacity: 0, transform: 'scale(0.95) translateY(-4px)' },
              to: { opacity: 1, transform: 'scale(1) translateY(0)' }
            }
          }}>

          {/* Year navigator */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
            <IconButton
              size="small"
              onClick={() => setViewYear((y) => y - 1)}
              sx={{ p: 0.5 }}>
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <Typography
              sx={{
                fontFamily: '"Onest", sans-serif',
                fontWeight: 700,
                fontSize: '0.95rem',
                color: 'text.primary',
                letterSpacing: '0.02em'
              }}>
              {viewYear}
            </Typography>
            <IconButton
              size="small"
              onClick={() => setViewYear((y) => y + 1)}
              disabled={viewYear >= maxYear}
              sx={{ p: 0.5 }}>
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Month grid — 4 columns × 3 rows */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px' }}>
            {MONTH_LABELS_ES.map((label, idx) => {
              const disabled = isDisabled(idx);
              const selected = isSelected(idx);
              return (
                <Box
                  key={idx}
                  onClick={() => !disabled && onChange(dayjs().year(viewYear).month(idx).startOf('month'))}
                  sx={{
                    py: 0.75,
                    borderRadius: '8px',
                    textAlign: 'center',
                    cursor: disabled ? 'default' : 'pointer',
                    bgcolor: selected ? '#0d9488' : 'transparent',
                    border: `1px solid ${selected ? '#0d9488' : 'transparent'}`,
                    transition: 'background 0.12s, border-color 0.12s',
                    '&:hover': disabled || selected ? {} : {
                      bgcolor: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(13,148,136,0.08)',
                      borderColor: '#0d9488'
                    }
                  }}>
                  <Typography
                    sx={{
                      fontSize: '0.78rem',
                      fontWeight: selected ? 700 : 500,
                      color: selected ? '#fff' : disabled ? 'text.disabled' : 'text.primary',
                      fontFamily: '"Source Sans 3", sans-serif',
                      letterSpacing: '0.01em'
                    }}>
                    {label}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
};

// ─── Metric Card ──────────────────────────────────────────────────────────────
const MetricCard = ({ title, value, subtitle, icon, color, onClick }) => (
  <Card
    onClick={onClick}
    sx={{
      p: 2.5,
      cursor: onClick ? 'pointer' : 'default',
      transition: 'transform 0.18s ease, box-shadow 0.18s ease',
      '&:hover': onClick ? { transform: 'translateY(-3px)', boxShadow: `0 8px 24px ${color}22` } : {}
    }}>
    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
      <Box>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
          }}>
          {title}
        </Typography>
        <Typography
          variant="h5"
          sx={{ color, fontWeight: 700, fontFamily: '"Onest", sans-serif', mt: 0.5, mb: subtitle ? 0.5 : 0 }}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      <Box sx={{ p: 1.2, background: `${color}18`, borderRadius: '10px', color }}>
        {React.cloneElement(icon, { sx: { fontSize: 22 } })}
      </Box>
    </Box>
  </Card>
);

const EmptyState = () => (
  <Box display="flex" alignItems="center" justifyContent="center" sx={{ height: 200 }}>
    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
      Sin datos para este período
    </Typography>
  </Box>
);

export default Dashboard;
